import { devError } from '../../../../../utils/devLog';
/**
 * D12 Lakshmi Dwadashamsha Division Attributes Data
 * Based on /Users/vn/src/hora/docs/new/lakshmi_dvadashasha_table.md
 * 30 divisions of 12° each (360° / 30 = 12°) from Surya position
 */

export interface LakshmiDwadashamshaAttributes {
  part: number;
  quality_english: string;
  quality_sanskrit?: string;
  description: string;
}

/**
 * D12 Lakshmi Dwadashamsha division attributes (12 qualities of Lakshmi)
 * 30 parts of 12° each, cycling through 12 Lakshmi qualities
 * Starting from current Surya position (Surya = 0°, always Part 1)
 */
export const LAKSHMI_DWADASHAMSHA_ATTRIBUTES: LakshmiDwadashamshaAttributes[] = [
  {
    part: 1,
    quality_english: "Devotion",
    quality_sanskrit: "Преданностью",
    description: "Loyalty and dedication"
  },
  {
    part: 2,
    quality_english: "Pleasant Speech",
    quality_sanskrit: "Приятной речью",
    description: "Ability to speak softly and pleasantly"
  },
  {
    part: 3,
    quality_english: "Thriftiness",
    quality_sanskrit: "Бережливостью",
    description: "Ability to save and preserve resources"
  },
  {
    part: 4,
    quality_english: "Maintaining Purity",
    quality_sanskrit: "Поддержанием чистоты",
    description: "Physical and spiritual cleanliness"
  },
  {
    part: 5,
    quality_english: "Fruitfulness",
    quality_sanskrit: "Плодотворностью",
    description: "Capacity for creation and creativity"
  },
  {
    part: 6,
    quality_english: "Absence of Quarrelsomeness",
    quality_sanskrit: "Отсутствием склочности",
    description: "Peacefulness and absence of conflict"
  },
  {
    part: 7,
    quality_english: "Ability to Give Joy",
    quality_sanskrit: "Умением дарить радость",
    description: "Capacity to bring happiness to others"
  },
  {
    part: 8,
    quality_english: "Ability to Restrain",
    quality_sanskrit: "Умением воздерживаться",
    description: "Self-control and moderation"
  },
  {
    part: 9,
    quality_english: "Following Dharma",
    quality_sanskrit: "Следованием Дхарме",
    description: "Observance of spiritual principles"
  },
  {
    part: 10,
    quality_english: "Skill in Household Management",
    quality_sanskrit: "Умелостью в домашнем хозяйстве",
    description: "Mastery in home management"
  },
  {
    part: 11,
    quality_english: "Freedom from Greed",
    quality_sanskrit: "Свободой от алчности",
    description: "Absence of greed"
  },
  {
    part: 12,
    quality_english: "Mercy/Compassion",
    quality_sanskrit: "Милосердием",
    description: "Compassion and kindness"
  }
];

/**
 * Parse longitude from position string format "rasi|degrees|longitude|speed"
 * @param position - Position string from API
 * @returns Longitude in degrees
 */
export function parseLongitudeFromPosition(position: string): number {
  const parts = position.split('|');
  if (parts.length >= 3) {
    return parseFloat(parts[2]);
  }
  return 0;
}

/**
 * Get Surya (Sun) longitude from API response using global degrees
 * @param apiResponse - JatakaResponse containing planet positions
 * @returns Surya longitude in degrees, or 0 if not found
 */
export function getSuryaLongitudeFromApi(apiResponse: any): number {
  if (!apiResponse?.graha_positions?.planets) return 0;

  const suryaPlanet = apiResponse.graha_positions.planets.find(
    (planet: any) => planet.short_name === 'Sy' || planet.name === 'Surya'
  );

  // Parse longitude from position string (contains global degrees)
  if (suryaPlanet?.position) {
    return parseLongitudeFromPosition(suryaPlanet.position);
  }

  // CRITICAL: Without Surya position, D12 calculations are invalid - invariant violation!
  devError('D12 Lakshmi: Surya position not found in API response - calculations will be incorrect');
  return 0;
}

/**
 * Calculate which D12 part (1-12) a planet falls into relative to Surya position
 * Uses global degrees from API. Surya becomes 0° reference point.
 * 30 parts of 12° each, cycling through 12 Lakshmi qualities
 * @param planetLongitude - Planet longitude in global degrees (from API)
 * @param suryaLongitude - Surya longitude in global degrees (from API)
 * @returns Part number (1-12) - Lakshmi quality index
 */
export function getLongitudeD12Part(planetLongitude: number, suryaLongitude: number = 0): number {
  // Normalize both longitudes to 0-360 range
  const normalizedPlanetLongitude = ((planetLongitude % 360) + 360) % 360;
  const normalizedSuryaLongitude = ((suryaLongitude % 360) + 360) % 360;

  // Calculate longitude relative to Surya (Surya becomes 0° reference)
  const adjustedLongitude = ((normalizedPlanetLongitude - normalizedSuryaLongitude + 360) % 360);

  // Each part is 12°, divided into 12 degrees (1° each)
  // Degree within the 12° part determines Lakshmi quality (1-12)
  const degreeInPart = Math.floor(adjustedLongitude % 12) + 1; // 1-12

  // Return Lakshmi quality based on degree position within part (1-12)
  return degreeInPart;
}